import * as React from 'react';

import {
    ApplicationHelpType,
    AssertionDescriptorCategories,
    AssertionDescriptorUsageTypesEnum,
    CommunicationProtocolAssertionDescriptors,
    DataScopesEnum,
    DocumentExportFormat,
    EntityTypes,
    LookupDataEnums,
    NoteCategories,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    OperatingMechanisms,
    StrategyTypes,
    SystemGeneratedDocumentsTypesEnum,
    UserRightsEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import { DocumentSubTypeEnum, DocumentTypeEnum } from '../../utilities/DbConstants';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
} from '../../interfaces/IAssertionDescriptor';
import {
    INoteCategoryModel,
    INoteCategoryTypeModel,
    INoteModel,
    INotesUICustomization,
} from '../../interfaces/INote';
import { Modal, Stack } from 'react-bootstrap';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from '../../components/shared/dialog';
import Common from '../../stores/Common';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { EditNarrativeModalExecutionPlan } from './EditNarrativeModal';
import EntityAssertionControl from '../../components/shared/EntityAssertionControl';
import { ExportFileButton } from '../../components/shared/ExportFileButton';
import { Help } from '../../components/shared/Help';
import { IOrganizationModel } from '../../interfaces/IOrganization';
import { IReportTypeModel } from '../../interfaces/Report/IReport';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../utilities/NoteHelper';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import Sort from '../../stores/Sort';
import { SortableHeader } from '../../components/shared/SortableHeader';
import ValidateUtils from '../../shared/validations';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useZoneContext } from '../../contexts/useZoneContext';

interface IExecutionPlanProps {
    assertionDescriptorsStrategy: IAssertionDescriptorModel[];
    assertionDescriptorsSignificantChanges: IAssertionDescriptorModel[];
    user: IUserModel;
    refreshIdentifier?: string;
    zoneContext: ZoneContextType;
}

interface IExecutionPlanState {
    assertions: IEntityAssertionModel[];
    currentNarrativeCaseAssertionOnEditMode?: IEntityAssertionModel;
    defaultOrganization: IOrganizationModel;
    loggedInUserDataScopeId: number;
    noteCategoryTypes: INoteCategoryTypeModel[];
    openNoteEditor?: boolean;
    originalAssertions: IEntityAssertionModel[];
    originalSignificantChanges: INoteModel[];
    pendingResponse: boolean;
    readOnly: boolean;
    reportType: IReportTypeModel;
    selectedSignificantChange?: INoteModel;
    selectedSignificantChangeToDelete?: INoteModel;
    showConfirmRemoveSignificantChangeDialog?: boolean;
    showSignificantChanges?: boolean;
    significantChanges: INoteModel[];
    validation: IValidation;
}

export class ExecutionPlan extends React.Component<IExecutionPlanProps, IExecutionPlanState> {
    constructor(props: any) {
        super(props);

        this.state = {
            pendingResponse: true,
            readOnly: true,
            validation: {},
            assertions: [],
            originalAssertions: [],
            noteCategoryTypes: [],
            defaultOrganization: {},
            significantChanges: [],
            originalSignificantChanges: [],
            reportType: { id: 0 },
            loggedInUserDataScopeId: 0,
        };

        this.getEntityAssertionMatch = this.getEntityAssertionMatch.bind(this);
    }

    componentDidMount() {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.StrategyExecutionPlan,
                this.props.user,
                this.props.zoneContext.zoneGuid
            )
        )
            window.location.assign(LocalRoutes.AccessDenied);
        this.loadLoggedInUserDefaultOrganization();
    }

    componentDidUpdate(prevProps: IExecutionPlanProps) {
        if (this.props.refreshIdentifier != prevProps.refreshIdentifier) {
            this.setState(
                {
                    pendingResponse: true,
                    readOnly: true,
                    validation: {},
                    assertions: [],
                    originalAssertions: [],
                    noteCategoryTypes: [],
                    defaultOrganization: {},
                    significantChanges: [],
                    originalSignificantChanges: [],
                    reportType: { id: 0 },
                    loggedInUserDataScopeId: 0,
                    openNoteEditor: false,
                    selectedSignificantChange: undefined,
                    showSignificantChanges: false,
                },
                this.loadLoggedInUserDefaultOrganization
            );
        }
    }

    loadLoggedInUserDefaultOrganization = async () => {
        const response =
            await this.props.zoneContext.zoneApiClient.getCurrentUserOrganizationFromServer(
                this.props.zoneContext.zoneGuid
            );
        if (!response) return;
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        this.setState(
            {
                defaultOrganization: response.payload!,
                loggedInUserDataScopeId: loggedInUserDataScopeId,
            },
            this.loadExecutionPlanAssertions
        );
    };

    loadExecutionPlanAssertions = async () => {
        this.setState({ pendingResponse: true });

        const response =
            await this.props.zoneContext.zoneApiClient.getExecutionPlanEntityAssertions();
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        if (response.payload) {
            this.setState(
                {
                    assertions: response.payload,
                    originalAssertions: cloneDeep(response.payload),
                    readOnly: true,
                },
                this.loadNoteCategoryTypes
            );
        }
    };

    loadNoteCategoryTypes = async () => {
        const response = await this.props.zoneContext.zoneApiClient.getNoteCategoryTypes(
            LookupDataEnums.SignificantChangesCategories
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        if (response.payload) {
            this.setState(
                { noteCategoryTypes: response.payload, readOnly: true },
                this.loadSignificantChanges
            );
        }
    };

    loadSignificantChanges = async () => {
        const response = await this.props.zoneContext.zoneApiClient.getNotes(
            NoteTypes.StrategySignificantChange,
            NoteCategories.StrategySignificantChanges.toString()
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }
        if (response.payload) {
            this.setState({
                significantChanges: response.payload,
                originalSignificantChanges: response.payload,
                pendingResponse: false,
                readOnly: true,
            });
        }
    };

    getHelpText = (originalText?: string) => {
        let text = '';
        if (originalText) {
            const temp = originalText.split('-');
            for (let i = 0; i < temp.length; i++) {
                if (temp[i]) text += '- ' + temp[i] + '\n';
            }
        }

        return text;
    };

    getLastUpdated = () => {
        if (this.state.assertions.filter((x) => x.modifiedDate).length > 0)
            return (
                'Last Updated ' +
                Common.dateTimeFormatToLocal(
                    this.state.assertions
                        .filter((x) => x.modifiedDate)
                        .sort(Sort.compareDate('modifiedDate', 'desc'))[0].modifiedDate
                )
            );
        else return '';
    };

    save = async () => {
        this.setState({ pendingResponse: true, validation: {} });
        const response = await this.props.zoneContext.zoneApiClient.saveEntityAssertions(
            this.state.assertions
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        this.loadExecutionPlanAssertions();
    };

    onClickViewAddEditNotesSection = () => {
        this.setState({ showSignificantChanges: !this.state.showSignificantChanges });
    };

    handleAddNote = () => {
        const noteCategory: INoteCategoryModel = { id: NoteCategories.StrategySignificantChanges };
        const currentNote: INoteModel = {
            type: { id: NoteTypes.StrategySignificantChange },
            status: { id: NoteStatusTypes.Open },
            organizationGuid: this.state.defaultOrganization.guid,
            categories: [noteCategory],
            purpose: { id: NotePurposeTypesEnum.Info },
            originStrategyType: StrategyTypes.ExecutionPlan.Value,
        };

        this.setState({
            openNoteEditor: true,
            selectedSignificantChange: currentNote,
            showSignificantChanges: false,
        });
    };

    handleEditNote = (e: React.MouseEvent<HTMLButtonElement>, noteGuidToEdit: string) => {
        if (!this.state.significantChanges) return;

        const currentNote = this.state.significantChanges.find((x) => x.guid == noteGuidToEdit);
        if (currentNote) {
            const noteWithStrategyType = {
                ...currentNote,
                originStrategyType: StrategyTypes.ExecutionPlan.Value,
            };

            this.setState({
                selectedSignificantChange: noteWithStrategyType,
                openNoteEditor: true,
                showSignificantChanges: false,
            });
        }
    };

    refreshParent = (notes: INoteModel[]) => {
        this.setState({ significantChanges: cloneDeep(notes) });
    };

    handleCancelNoteEditor = () => {
        this.setState({
            significantChanges: cloneDeep(this.state.originalSignificantChanges),
            selectedSignificantChange: undefined,
            openNoteEditor: false,
            showSignificantChanges: true,
        });
    };

    handleSaveNoteComplete = (noteItem: INoteModel) => {
        let significantChanges = this.state.significantChanges || [];
        let matchFound = false;
        for (let i = 0; i < significantChanges.length; i++) {
            if (significantChanges[i].guid == noteItem.guid) {
                matchFound = true;
                significantChanges[i] = cloneDeep(noteItem);
                break;
            }
        }

        if (!matchFound) significantChanges.push(cloneDeep(noteItem));
        significantChanges = significantChanges.sort(
            NoteHelper.sortNotesByStarredAndThenByModifiedDate
        );
        this.setState({
            pendingResponse: false,
            openNoteEditor: false,
            significantChanges: significantChanges,
            originalSignificantChanges: cloneDeep(significantChanges),
            selectedSignificantChange: undefined,
            showSignificantChanges: true,
        });
    };

    getNotesUICustomization = () => {
        const config: INotesUICustomization = {
            hideCategories: true,
            contentHeaderText: 'Impact',
            displayAdditionalInfoAsColumns: true,
            hideFilterByCategory: true,
            hideAuditFields: true,
            hideExport: true,
        };

        return config;
    };

    getEntityAssertionMatch(
        assertionDescriptorGuid: string,
        entityTypeId: number,
        entityId?: number
    ) {
        if (entityId && this.state.assertions) {
            return this.state.assertions.find(
                (x) =>
                    x.assertionDescriptorGuid!.toUpperCase() ===
                        assertionDescriptorGuid.toUpperCase() &&
                    x.entityType.id === entityTypeId &&
                    x.entityId === entityId
            );
        } else {
            const foundAssertion = this.state.assertions.find(
                (x) =>
                    x.assertionDescriptorGuid!.toUpperCase() ===
                        assertionDescriptorGuid.toUpperCase() && x.entityType.id == entityTypeId
            );

            return foundAssertion;
        }
    }

    finalizeChange = (modifiedAssertions: IEntityAssertionModel[]) => {
        if (modifiedAssertions.length == 1) {
            const assertions = this.state.assertions;
            const match = assertions.filter(
                (x) =>
                    x.guid === modifiedAssertions[0].guid &&
                    x.assertionDescriptorGuid === modifiedAssertions[0].assertionDescriptorGuid &&
                    x.entityType &&
                    modifiedAssertions[0].entityType &&
                    x.entityType.id === modifiedAssertions[0].entityType.id &&
                    x.entityId === modifiedAssertions[0].entityId
            );
            if (match.length > 0) {
                match[0].textValue = modifiedAssertions[0].textValue;
                match[0].booleanValue = modifiedAssertions[0].booleanValue;
                match[0].numericValue = modifiedAssertions[0].numericValue;
                match[0].dateTimeValue = modifiedAssertions[0].dateTimeValue;
                match[0].assertionDescriptorValueGuid =
                    modifiedAssertions[0].assertionDescriptorValueGuid;

                this.setState({
                    assertions: assertions,
                    currentNarrativeCaseAssertionOnEditMode: undefined,
                });
            }
        }
    };

    handleNarrativeFieldEdit = (item: IEntityAssertionModel) => {
        const match = this.state.assertions.find(
            (x) =>
                x.guid === item.guid &&
                x.assertionDescriptorGuid === item.assertionDescriptorGuid &&
                x.entityType &&
                item.entityType &&
                x.entityType.id === item.entityType.id &&
                x.entityId === item.entityId
        );
        if (match)
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: cloneDeep(match),
            });
    };

    handleClearNarrativeFieldEdit = () => {
        if (this.state.currentNarrativeCaseAssertionOnEditMode) {
            const currentNarrativeCaseAssertionOnEditMode =
                this.state.currentNarrativeCaseAssertionOnEditMode;
            currentNarrativeCaseAssertionOnEditMode.textValue = '';
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: currentNarrativeCaseAssertionOnEditMode,
            });
        }
    };

    handleNarrativeFieldTextChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        if (this.state.currentNarrativeCaseAssertionOnEditMode) {
            const currentNarrativeCaseAssertionOnEditMode =
                this.state.currentNarrativeCaseAssertionOnEditMode;
            currentNarrativeCaseAssertionOnEditMode.textValue = event.target.value;
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: currentNarrativeCaseAssertionOnEditMode,
            });
        }
    };

    sortData = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        if (this.state.significantChanges) {
            let significantChanges: INoteModel[] = [];
            if (key == 'date') {
                // HACK. Only for Signiticant Changes List, Date = Associated Entity Assertion and NOT Modified Date
                const significantChangesTemp: INoteModel[] = JSON.parse(
                    JSON.stringify(this.state.significantChanges)
                );
                {
                    significantChangesTemp.map((item: INoteModel) => {
                        item.modifiedDate = Common.dateFormat(
                            NoteHelper.getAssertionValue(
                                item,
                                'DATE',
                                this.props.assertionDescriptorsSignificantChanges
                            )
                        );
                    });
                }

                const sortedData = significantChangesTemp.sort(
                    Sort.compareDate('modifiedDate', subKey, order)
                );
                sortedData.map((item: INoteModel) => {
                    significantChanges.push(
                        this.state.significantChanges.find((x) => x.guid === item.guid)!
                    );
                });
            } else {
                significantChanges = this.state.significantChanges.sort(
                    Sort.compareValues(key, subKey, order, subGrandKey)
                );
            }

            this.setState({ significantChanges: significantChanges });
        }
    };

    showConfirmRemoveDailog = (note: INoteModel) => {
        this.setState({
            showConfirmRemoveSignificantChangeDialog: true,
            selectedSignificantChangeToDelete: note,
        });
    };

    cancelRemoveSignificantChange = () => {
        this.setState({
            showConfirmRemoveSignificantChangeDialog: false,
            selectedSignificantChangeToDelete: undefined,
        });
    };

    deleteNote = async () => {
        if (!this.state.selectedSignificantChangeToDelete) return;

        let key;
        if (this.state.selectedSignificantChangeToDelete.type!.id == NoteTypes.CaseNote) {
            key = this.state.selectedSignificantChangeToDelete.caseGuid;
        }

        const response = await this.props.zoneContext.zoneApiClient.deleteNote(
            this.state.selectedSignificantChangeToDelete!.guid!,
            key
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
            });
            return;
        }

        const updatedSignificantChanges = this.state.significantChanges.filter(
            (x) => x.guid != this.state.selectedSignificantChangeToDelete!.guid!
        );
        this.setState({
            significantChanges: updatedSignificantChanges,
            originalSignificantChanges: cloneDeep(updatedSignificantChanges),
            showConfirmRemoveSignificantChangeDialog: false,
        });
    };

    handleSignificantChangesExportError = () => {
        const validation = this.state.validation;
        validation.significantChanges = [DisplayMessages.UnexpectedError];
        this.setState({ pendingResponse: false, validation: validation });
    };

    handleExportError = () => {
        const validation = this.state.validation;
        validation.model = [DisplayMessages.UnexpectedError];
        this.setState({ pendingResponse: false, validation: validation });
    };

    getEntityAssertionsForEntityAssertionsControl = (
        assertionDescriptorGuid: string,
        entityType: EntityTypes,
        mechanism?: OperatingMechanisms
    ) => {
        const match = this.getEntityAssertionMatch(assertionDescriptorGuid, entityType, mechanism);

        return match ? [match] : [];
    };

    render() {
        if (this.state.pendingResponse) return <Loader />;

        const strategyAssertions = this.state.assertions.filter((x) => {
            const foundAD = this.props.assertionDescriptorsStrategy.find(
                (ad) => ad.guid === x.assertionDescriptorGuid
            );

            return (
                foundAD?.category?.id == AssertionDescriptorCategories.Strategy_Principles.Value &&
                x.entityType.id == EntityTypes.Organization
            );
        });

        return (
            <>
                {this.state.openNoteEditor && this.state.selectedSignificantChange && (
                    <MainContentOverlayComponent>
                        <NoteEditor
                            noteCategoryTypes={this.state.noteCategoryTypes}
                            currentNote={this.state.selectedSignificantChange}
                            onSaveComplete={this.handleSaveNoteComplete}
                            onCancel={this.handleCancelNoteEditor}
                            notesUICustomization={this.getNotesUICustomization()}
                            enableNoteAssociations={true}
                            noteAssociationTitle="Associated Topic(s)"
                            noteAssociationIcon='fa-users'
                            noteAssociationEntityType={{ id: EntityTypes.AssertionDescriptor }}
                            noteAssociationEntityItems={NoteHelper.getAssociatedAssertionDescriptorListForSignificantChanges(
                                this.state.assertions,
                                this.props.assertionDescriptorsStrategy
                            )}
                            strategyTypeId={StrategyTypes.ExecutionPlan.Value}
                            originStrategyType={StrategyTypes.ExecutionPlan.Value}
                        />
                    </MainContentOverlayComponent>
                )}

                <PageWithAffixedHeader
                    printHide={this.state.openNoteEditor}
                    headerComponent={
                        <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                            <h1>
                                Execution Plan
                                <span className=" font-size-xs ms-2">{this.getLastUpdated()}</span>
                                {Authorization.userHasRight(
                                    UserRightsEnum.EditExecutionPlan,
                                    this.props.user,
                                    this.props.zoneContext.zoneGuid
                                ) ? (
                                    <></>
                                ) : null}
                            </h1>
                            <div style={{ paddingRight: '1em' }}>
                                {this.state.readOnly ? (
                                    <span>
                                        {Authorization.userHasRight(
                                            UserRightsEnum.ViewStrategicPlan,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) ||
                                        Authorization.userHasRight(
                                            UserRightsEnum.EditStrategicPlan,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) ? (
                                            <button
                                                className="btn btn-no-bg float-end text-gray -only"
                                                onClick={() =>
                                                    this.setState({
                                                        showSignificantChanges: true,
                                                    })
                                                }
                                            >
                                                <i className="fal fa-directions" />
                                                &nbsp;Significant Changes
                                            </button>
                                        ) : null}
                                        {Authorization.userHasRight(
                                            UserRightsEnum.EditExecutionPlan,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) ? (
                                            <>
                                                <ExportFileButton
                                                    exportFormat={DocumentExportFormat.PDF}
                                                    url={'/' + ApiRoutes.ExportReport}
                                                    reportType={{
                                                        id: SystemGeneratedDocumentsTypesEnum.ExecutionPlanExport
                                                            .Value,
                                                    }}
                                                    onError={this.handleExportError}
                                                    icon="fa-file-pdf"
                                                />
                                                <button
                                                    className="btn btn-no-bg float-end text-gray"
                                                    onClick={() =>
                                                        this.setState({ readOnly: false })
                                                    }
                                                >
                                                    <i className="fal fa-pen" />
                                                    &nbsp;Edit
                                                </button>
                                            </>
                                        ) : null}
                                    </span>
                                ) : Authorization.userHasRight(
                                      UserRightsEnum.EditExecutionPlan,
                                      this.props.user,
                                      this.props.zoneContext.zoneGuid
                                  ) ? (
                                    <>
                                        <span>
                                            <button
                                                className="btn btn-default float-end"
                                                onClick={() =>
                                                    this.setState({
                                                        assertions: this.state.originalAssertions,
                                                        readOnly: true,
                                                    })
                                                }
                                            >
                                                Cancel
                                            </button>
                                            <input
                                                type="button"
                                                className="btn btn-orange float-end "
                                                onClick={this.save}
                                                value="Save"
                                            />
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        </Stack>
                    }
                >
                    <span className="text-danger">{this.state.validation.model}</span>
                    <div className="panel panel-default thick-outer-border p-2">
                        <div className="panel-heading bold">Principles</div>
                        <div className="panel-body" style={{ backgroundColor: '#f5f5f5' }}>
                            {strategyAssertions.map(
                                (item: IEntityAssertionModel, index: number) => {
                                    const foundAD = this.props.assertionDescriptorsStrategy.find(
                                        (ad) => ad.guid === item.assertionDescriptorGuid
                                    );

                                    return (
                                        <div>
                                            <div className="row no-margin">
                                                <div className="col-sm-3">
                                                    <label className="text-gray">
                                                        {foundAD?.displayName}
                                                    </label>
                                                    {foundAD?.helpText && (
                                                        <Help
                                                            type={ApplicationHelpType.Info}
                                                            title={
                                                                foundAD?.displayName ??
                                                                foundAD.name ??
                                                                ''
                                                            }
                                                            helpText={foundAD.helpText}
                                                        />
                                                    )}
                                                    {Authorization.userHasAtLeastOneRight(
                                                        this.props.user,
                                                        [
                                                            UserRightsEnum.ViewStrategicPlan,
                                                            UserRightsEnum.EditStrategicPlan,
                                                        ],
                                                        this.props.zoneContext.zoneGuid
                                                    ) &&
                                                        this.state.significantChanges &&
                                                        NoteHelper.hasSignificantChange(
                                                            item.assertionDescriptorGuid!,
                                                            this.state.significantChanges
                                                        ) && (
                                                            <button
                                                                className="btn btn-no-bg"
                                                                onClick={() =>
                                                                    this.setState({
                                                                        showSignificantChanges: true,
                                                                    })
                                                                }
                                                            >
                                                                <i className="fal fa-lg fa-directions color-gray" />
                                                            </button>
                                                        )}
                                                </div>
                                                <div className="col-sm-9 horizontal-padding">
                                                    <EntityAssertionControl
                                                        assertionDescriptor={foundAD}
                                                        entityAssertions={[item]}
                                                        editMode={false}
                                                        assertionDescriptorUsageType={
                                                            AssertionDescriptorUsageTypesEnum.Strategy
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            {index !== strategyAssertions.length - 1 && (
                                                <hr className="thin-top-border strategic-plan-line-margin" />
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </div>

                    <table className="table mt-3">
                        <thead>
                            <tr>
                                <th className="col-sm-2">Operating Mechanisms</th>
                                <th className="col-sm-2">Client Service</th>
                                <th className="col-sm-2">Portfolio Management</th>
                                <th className="col-sm-2">Case Development</th>
                                <th className="col-sm-2">COEs</th>
                                <th className="col-sm-2">Local Counsel</th>
                            </tr>
                        </thead>
                        <tbody>
                            {strategyAssertions.map(
                                (item: IEntityAssertionModel, index: number) => {
                                    const foundAD = this.props.assertionDescriptorsStrategy.find(
                                        (ad) => ad.guid === item.assertionDescriptorGuid
                                    );

                                    return (
                                        <tr key={index}>
                                            <td className="col-sm-2 text-gray">
                                                {foundAD?.displayName}
                                            </td>
                                            <td className="col-sm-2">
                                                <EntityAssertionControl
                                                    assertionDescriptor={foundAD}
                                                    assertionDescriptorUsageType={
                                                        AssertionDescriptorUsageTypesEnum.Strategy
                                                    }
                                                    entityAssertions={this.getEntityAssertionsForEntityAssertionsControl(
                                                        item.assertionDescriptorGuid,
                                                        EntityTypes.OperatingMechanism,
                                                        OperatingMechanisms.ClientService
                                                    )}
                                                    editMode={!this.state.readOnly}
                                                    refreshChange={this.finalizeChange}
                                                    handleNarrativeFieldEdit={
                                                        this.handleNarrativeFieldEdit
                                                    }
                                                    narrativeFieldAsPopup
                                                />
                                            </td>
                                            <td className="col-sm-2">
                                                <EntityAssertionControl
                                                    assertionDescriptorUsageType={
                                                        AssertionDescriptorUsageTypesEnum.Strategy
                                                    }
                                                    assertionDescriptor={foundAD}
                                                    entityAssertions={this.getEntityAssertionsForEntityAssertionsControl(
                                                        item.assertionDescriptorGuid,
                                                        EntityTypes.OperatingMechanism,
                                                        OperatingMechanisms.PortfolioManagement
                                                    )}
                                                    editMode={!this.state.readOnly}
                                                    refreshChange={this.finalizeChange}
                                                    handleNarrativeFieldEdit={
                                                        this.handleNarrativeFieldEdit
                                                    }
                                                    narrativeFieldAsPopup
                                                />
                                            </td>
                                            <td className="col-sm-2">
                                                <EntityAssertionControl
                                                    assertionDescriptorUsageType={
                                                        AssertionDescriptorUsageTypesEnum.Strategy
                                                    }
                                                    assertionDescriptor={foundAD}
                                                    entityAssertions={this.getEntityAssertionsForEntityAssertionsControl(
                                                        item.assertionDescriptorGuid,
                                                        EntityTypes.OperatingMechanism,
                                                        OperatingMechanisms.CaseDevelopment
                                                    )}
                                                    editMode={!this.state.readOnly}
                                                    refreshChange={this.finalizeChange}
                                                    handleNarrativeFieldEdit={
                                                        this.handleNarrativeFieldEdit
                                                    }
                                                    narrativeFieldAsPopup
                                                />
                                            </td>
                                            <td className="col-sm-2">
                                                <EntityAssertionControl
                                                    assertionDescriptorUsageType={
                                                        AssertionDescriptorUsageTypesEnum.Strategy
                                                    }
                                                    assertionDescriptor={foundAD}
                                                    entityAssertions={this.getEntityAssertionsForEntityAssertionsControl(
                                                        item.assertionDescriptorGuid,
                                                        EntityTypes.OperatingMechanism,
                                                        OperatingMechanisms.COE
                                                    )}
                                                    editMode={!this.state.readOnly}
                                                    refreshChange={this.finalizeChange}
                                                    handleNarrativeFieldEdit={
                                                        this.handleNarrativeFieldEdit
                                                    }
                                                    narrativeFieldAsPopup
                                                />
                                            </td>
                                            <td className="col-sm-3">
                                                <EntityAssertionControl
                                                    assertionDescriptorUsageType={
                                                        AssertionDescriptorUsageTypesEnum.Strategy
                                                    }
                                                    assertionDescriptor={foundAD}
                                                    entityAssertions={this.getEntityAssertionsForEntityAssertionsControl(
                                                        item.assertionDescriptorGuid,
                                                        EntityTypes.OperatingMechanism,
                                                        OperatingMechanisms.LocalCounsel
                                                    )}
                                                    editMode={!this.state.readOnly}
                                                    refreshChange={this.finalizeChange}
                                                    handleNarrativeFieldEdit={
                                                        this.handleNarrativeFieldEdit
                                                    }
                                                    narrativeFieldAsPopup
                                                />
                                            </td>
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </table>

                    <table className="table">
                        <thead>
                            <tr>
                                <th colSpan={3}>Communication Protocol</th>
                            </tr>
                        </thead>
                        <tbody>
                            <DayToDay
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <WeeklyMeetings
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <MonthlyMeetings
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <QuarterlyMeetings
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <AnnualMeetings
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <WeeklyReports
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <MonthlyReports
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <QuarterlyReports
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                            <AnnualReports
                                assertionDescriptors={this.props.assertionDescriptorsStrategy}
                                getEntityAssertion={this.getEntityAssertionMatch}
                                onFinalizeChange={this.finalizeChange}
                                onNarrativeFieldEdit={this.handleNarrativeFieldEdit}
                                readonly={this.state.readOnly}
                                significantChanges={this.state.significantChanges}
                                user={this.props.user}
                            />
                        </tbody>
                    </table>

                    {this.state.currentNarrativeCaseAssertionOnEditMode && (
                        <EditNarrativeModalExecutionPlan
                            onFinalizeChange={this.finalizeChange}
                            assertionDescriptors={this.props.assertionDescriptorsStrategy}
                            entityAssertion={this.state.currentNarrativeCaseAssertionOnEditMode}
                            onCancel={() =>
                                this.setState({
                                    currentNarrativeCaseAssertionOnEditMode: undefined,
                                })
                            }
                            onClear={this.handleClearNarrativeFieldEdit}
                            onNarrativeFieldTextChange={this.handleNarrativeFieldTextChange}
                            validation={this.state.validation}
                        />
                    )}

                    {this.state.showSignificantChanges && (
                        <Modal
                            centered
                            size="xl"
                            show={true}
                            onHide={() =>
                                this.setState({
                                    showSignificantChanges: false,
                                    selectedSignificantChange: undefined,
                                    openNoteEditor: false,
                                })
                            }
                            backdrop={false}
                            dialogClassName="add-task-dialog"
                        >
                            <Modal.Header>
                                <Modal.Title className="w-100">
                                    <div className="row">
                                        <div className="col-sm-6">Significant Changes</div>
                                        <div className="col-sm-6">
                                            {Authorization.userHasRight(
                                                UserRightsEnum.EditStrategicPlan,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            ) ? (
                                                <button
                                                    className="btn-no-bg float-end  "
                                                    onClick={this.handleAddNote}
                                                >
                                                    <span className="btn-green btn float-end btn-icon">
                                                        <i className="fal fa-lg fa-plus color-white" />
                                                    </span>
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <table className="table">
                                    <thead>
                                        <tr style={{ verticalAlign: 'middle' }}>
                                            <th className="col-sm-2 ">Change</th>
                                            <SortableHeader
                                                headerText="Date"
                                                sortKey="date"
                                                onSort={this.sortData}
                                                thClassName="col-sm-1"
                                            />
                                            <th>Impact</th>
                                            <th className="col-sm-2 ">Associated Topic(s)</th>
                                            {Authorization.userHasRight(
                                                UserRightsEnum.EditStrategicPlan,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            ) && <th />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.significantChanges.map((item: INoteModel) => {
                                            const showEditNote =
                                                Authorization.userHasRight(
                                                    UserRightsEnum.EditCaseNote,
                                                    this.props.user,
                                                    this.props.zoneContext.zoneGuid
                                                ) &&
                                                (this.state.loggedInUserDataScopeId !==
                                                    DataScopesEnum.LocalBasic.Value ||
                                                    item.allowLocalEdit);

                                            return (
                                                <tr key={item.guid}>
                                                    <td className="col-sm-3">
                                                        {NoteHelper.getAssertionValue(
                                                            item,
                                                            'CHANGE',
                                                            this.props
                                                                .assertionDescriptorsSignificantChanges
                                                        )}
                                                    </td>
                                                    <td className="col-sm-1  ">
                                                        {Common.dateFormat(
                                                            NoteHelper.getAssertionValue(
                                                                item,
                                                                'DATE',
                                                                this.props
                                                                    .assertionDescriptorsSignificantChanges
                                                            )
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.content
                                                            ?.split('\n')
                                                            .map((item, key) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {item}
                                                                        <br />
                                                                    </span>
                                                                );
                                                            })}
                                                    </td>
                                                    <td className="col-sm-3  ">
                                                        {NoteHelper.getAssociatedTopicsForSignificantChanges(
                                                            item
                                                        )
                                                            .split('\n')
                                                            .map((item, key) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {item}
                                                                        <br />
                                                                    </span>
                                                                );
                                                            })}
                                                    </td>
                                                    {Authorization.userHasRight(
                                                        UserRightsEnum.EditStrategicPlan,
                                                        this.props.user,
                                                        this.props.zoneContext.zoneGuid
                                                    ) ? (
                                                        <td className="col-sm-2">
                                                            <button
                                                                className="btn btn-no-bg float-end"
                                                                onClick={() => {
                                                                    this.showConfirmRemoveDailog(
                                                                        item
                                                                    );
                                                                }}
                                                            >
                                                                <i className="fal fa-lg fa-trash-alt"></i>
                                                            </button>

                                                            {showEditNote ? (
                                                                <button
                                                                    className="btn btn-no-bg float-end"
                                                                    onClick={(
                                                                        e: React.MouseEvent<HTMLButtonElement>
                                                                    ) => {
                                                                        this.handleEditNote(
                                                                            e,
                                                                            item.guid!
                                                                        );
                                                                    }}
                                                                >
                                                                    <i className="fal fa-lg fa-pen" />
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className="dialog-btn-div ">
                                    <button
                                        className="btn btn-black float-end"
                                        onClick={() =>
                                            this.setState({
                                                showSignificantChanges: false,
                                                selectedSignificantChange: undefined,
                                                openNoteEditor: false,
                                            })
                                        }
                                    >
                                        OK
                                    </button>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    <CheckmateDialog
                        isShowingModal={
                            this.state.showConfirmRemoveSignificantChangeDialog || false
                        }
                        body="Are you sure you want to delete the selected item?"
                        handleClose={this.cancelRemoveSignificantChange}
                        handleConfirm={this.deleteNote}
                        confirmText="Delete"
                        cancelText="Cancel"
                        confirmButtonClassName="btn btn-danger"
                        dialogClassName="confirm-document-delete-dialog"
                        closeButtonClassName="btn btn-default"
                        title="Delete"
                    />
                </PageWithAffixedHeader>
            </>
        );
    }
}

export interface ICommunicationProtocolRowProps {
    assertionDescriptorGuid?: string;
    assertionDescriptors: IAssertionDescriptorModel[];
    getEntityAssertion: (
        assertionDescriptorId: string,
        entityTypeId: number,
        entityId?: number
    ) => IEntityAssertionModel | undefined;
    entityId?: number;
    helpTitle?: string;
    onFinalizeChange: (modifiedAssertions: IEntityAssertionModel[]) => void;
    onNarrativeFieldEdit: (entityAssertion: IEntityAssertionModel) => void;
    onSignificantChangeClick?: () => void;
    readonly?: boolean;
    rowGroupLabel?: string;
    rowLabel?: string;
    significantChanges: INoteModel[];
    user: IUserModel;
}

function CommunicationProtocolRow(props: ICommunicationProtocolRowProps) {
    const zoneContext = useZoneContext();

    if (!props.assertionDescriptorGuid) {
        return null;
    }

    const entityAssertion = props.getEntityAssertion(
        props.assertionDescriptorGuid,
        EntityTypes.Organization,
        props.entityId
    );

    const hasSignificantChange = NoteHelper.hasSignificantChange(
        props.assertionDescriptorGuid,
        props.significantChanges
    );

    const foundAD = props.assertionDescriptors.find(
        (ad) => ad.guid === entityAssertion?.assertionDescriptorGuid
    );

    return (
        <tr>
            <td className="col-sm-2 text-gray">{props.rowGroupLabel}</td>
            <td className="col-sm-1 text-gray">
                {props.rowLabel}
                {foundAD?.helpText && (
                    <Help
                        type={ApplicationHelpType.Info}
                        title={props.helpTitle ?? ''}
                        helpText={foundAD?.helpText ?? ''}
                    />
                )}
                {Authorization.userHasAtLeastOneRight(
                    props.user,
                    [UserRightsEnum.ViewStrategicPlan, UserRightsEnum.EditStrategicPlan],
                    zoneContext.zoneGuid
                ) &&
                    hasSignificantChange && (
                        <button className="btn btn-no-bg" onClick={props.onSignificantChangeClick}>
                            <i className="fal fa-lg fa-directions color-gray" />
                        </button>
                    )}
            </td>
            <td>
                <EntityAssertionControl
                    assertionDescriptor={foundAD}
                    assertionDescriptorUsageType={AssertionDescriptorUsageTypesEnum.Strategy}
                    entityAssertions={entityAssertion ? [entityAssertion] : []}
                    editMode={!props.readonly}
                    refreshChange={props.onFinalizeChange}
                    handleNarrativeFieldEdit={props.onNarrativeFieldEdit}
                    narrativeFieldAsPopup
                />
            </td>
        </tr>
    );
}

function DayToDay(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowGroupLabel="Day-to-day"
            helpTitle="Day-to-day"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.Daily.Guid}
        />
    );
}

function WeeklyMeetings(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Week"
            rowGroupLabel="Meetings"
            helpTitle="Week"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.WeeklyMeetings.Guid}
        />
    );
}

function MonthlyMeetings(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Month"
            helpTitle="Monthly Meetings"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.MonthlyMeetings.Guid}
        />
    );
}

function QuarterlyMeetings(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Quarter"
            helpTitle="Quarterly Meetings"
            assertionDescriptorGuid={
                CommunicationProtocolAssertionDescriptors.QuarterlyMeetings.Guid
            }
        />
    );
}

function AnnualMeetings(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Annual"
            helpTitle="Annual Reports"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.AnnualMeetings.Guid}
        />
    );
}

function WeeklyReports(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Week"
            helpTitle="Weekly Reports"
            rowGroupLabel="Reports"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.WeeklyReports.Guid}
        />
    );
}

function MonthlyReports(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Month"
            helpTitle="Monthly Reports"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.MonthlyReports.Guid}
        />
    );
}

function QuarterlyReports(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Quarter"
            helpTitle="Quarterly Reports"
            assertionDescriptorGuid={
                CommunicationProtocolAssertionDescriptors.QuarterlyReports.Guid
            }
        />
    );
}

function AnnualReports(props: ICommunicationProtocolRowProps) {
    return (
        <CommunicationProtocolRow
            {...props}
            rowLabel="Annual"
            helpTitle="Annual Reports"
            assertionDescriptorGuid={CommunicationProtocolAssertionDescriptors.AnnualReports.Guid}
        />
    );
}
